import { Inject, Injectable, OnDestroy, Optional } from '@angular/core';
import {
  BehaviorSubject,
  distinctUntilChanged,
  map,
  Subscription,
  filter,
} from 'rxjs';
import { ConfigApi } from '../api/config.api';
import { NEVER_TOKEN } from '../constants/injection-tokens';
import { Settings } from '../models/settings.model';
import { AbstractFacade } from './abstract.facade';
import { Store } from '@ngrx/store';

// 3 minutes
const SETTINGS_CACHE_VALIDITY = 300000;

interface SettingsState {
  settings: Settings;
  loading: boolean;
  loaded: boolean;
  lastLoadingTime: number | null;
}

@Injectable({
  providedIn: 'root',
})
export class SettingsFacade extends AbstractFacade implements OnDestroy {
  private state: SettingsState = {
    settings: null,
    loading: false,
    loaded: false,
    lastLoadingTime: null,
  };
  public store = new BehaviorSubject<SettingsState>(this.state);
  public state$ = this.store.asObservable();

  public readonly settings$ = this.state$.pipe(
    map((state) => state.settings),
    distinctUntilChanged()
  );
  public readonly loading$ = this.state$.pipe(
    map((state) => state.loading),
    distinctUntilChanged()
  );
  public readonly unit$ = this.settings$.pipe(
    filter((settings: Settings) => !!settings),
    map((settings: Settings) => settings?.unit ?? 'metric'),
    distinctUntilChanged()
  );
  private subscriptions: Subscription;

  constructor(
    private readonly configApi: ConfigApi,
    @Inject(NEVER_TOKEN) @Optional() protected readonly userId: number
  ) {
    super();
  }

  getState$() {
    return this.state$;
  }

  setState(newState: Partial<SettingsState>) {
    this.state = {
      ...this.state,
      ...newState,
    };
    this.store.next(this.state);
  }

  load() {
    if (this.state.loading) {
      return;
    }
    if (this.state.loaded || this.isCacheValid()) {
      return;
    }
    this.setState({
      loading: true,
    });
    this.subscriptions = this.configApi
      .getMySettings()
      .subscribe((settings: Settings) => {
        this.setState({
          settings,
          loading: false,
          loaded: true,
          lastLoadingTime: Date.now(),
        });
      });
  }

  private isCacheValid() {
    if (!this.state.lastLoadingTime) {
      return false;
    }
    const currentTime = Date.now();
    if (currentTime > this.state.lastLoadingTime + SETTINGS_CACHE_VALIDITY) {
      return false;
    }
    return true;
  }

  isLoaded() {
    return this.state.loaded;
  }

  ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
  }
}
